import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

export const STUDENT_LOGIN_SITE = 'https://mydigitalschools.com';

const NAVY = [46, 49, 145];
const BOX_LINE = [214, 218, 226];

const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 28;
const COL_GAP = 10;
const ROW_GAP = 14;
const CARD_W = (PAGE_W - MARGIN * 2 - COL_GAP) / 2;

export function buildStudentQrLoginUrl(userId, password) {
  const url = new URL('/q', STUDENT_LOGIN_SITE);
  url.searchParams.set('id', String(userId).trim());
  url.searchParams.set('p', String(password).trim());
  return url.toString();
}

export function rasterizeLogo(src, pixelSize = 480) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelSize;
      canvas.height = Math.round(pixelSize * (image.height / image.width || 1));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Could not load the logo'));
    image.src = src;
  });
}

export async function buildStudentLoginPdf(students, logoDataUrl) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
  const cards = [];

  for (const student of students) {
    const userId = String(student.userId || '').trim();
    const password = String(student.password || '').trim();
    let qr = null;
    if (userId && password) {
      qr = await QRCode.toDataURL(buildStudentQrLoginUrl(userId, password), {
        errorCorrectionLevel: 'M',
        margin: 0,
        width: 280,
        color: { dark: '#2E3191', light: '#FFFFFF' },
      });
    }
    cards.push({
      name: student.name || 'Unnamed',
      userId: userId || '—',
      password: password || '—',
      grade: student.grade || '—',
      qr,
    });
  }

  let cursorY = MARGIN;
  let firstPage = true;

  for (let i = 0; i < cards.length; i += 2) {
    const pair = cards.slice(i, i + 2);
    const rowH = Math.max(...pair.map((card) => measureCard(doc, card)));
    if (!firstPage && cursorY + rowH > PAGE_H - MARGIN) {
      doc.addPage();
      cursorY = MARGIN;
    }
    firstPage = false;

    pair.forEach((card, index) => {
      const x = MARGIN + index * (CARD_W + COL_GAP);
      drawCard(doc, card, x, cursorY, CARD_W, rowH, logoDataUrl);
    });
    cursorY += rowH + ROW_GAP;
  }

  return doc.output('arraybuffer');
}

function measureCard(doc, card) {
  const layout = cardLayout(doc, card);
  return layout.height;
}

function cardLayout(doc, card) {
  const pad = 14;
  const inner = CARD_W - pad * 2;
  const qrSize = 76;
  const textW = inner - qrSize - 10;
  const logoW = 74;
  const logoH = 72;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  const nameLines = doc.splitTextToSize(card.name, textW).slice(0, 2);
  const gradeLines = doc.splitTextToSize(card.grade, textW).slice(0, 2);

  const textBlock = 12 + nameLines.length * 14 + 2 + 12 + gradeLines.length * 14 + 6 + 11 + 12;
  const bottom = Math.max(textBlock, qrSize);
  const height = 12 + logoH + 8 + 12 + 28 + 7 + 12 + 28 + 8 + bottom + 12;

  return { pad, inner, qrSize, textW, logoW, logoH, nameLines, gradeLines, textBlock, bottom, height };
}

function drawCard(doc, card, x, y, w, h, logoDataUrl) {
  const layout = cardLayout(doc, card);
  const { pad, inner, qrSize, logoW, logoH, nameLines, gradeLines } = layout;
  let cy = y + 12;

  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', x + (w - logoW) / 2, cy, logoW, logoH);
  }
  cy += logoH + 8;

  cy = drawLabel(doc, 'ID:', x + pad, cy);
  cy = drawValueBox(doc, card.userId, x + pad, cy, inner);
  cy += 7;
  cy = drawLabel(doc, 'Password:', x + pad, cy);
  cy = drawValueBox(doc, card.password, x + pad, cy, inner);
  cy += 8;

  const blockTop = cy;
  cy = drawLabel(doc, 'Name:', x + pad, cy);
  cy = drawStrong(doc, nameLines, x + pad, cy);
  cy = drawLabel(doc, 'Grade:', x + pad, cy);
  cy = drawStrong(doc, gradeLines, x + pad, cy);
  cy += 4;
  drawHint(doc, 'Scan the QR code', x + pad, cy);
  cy += 11;
  drawHint(doc, 'and sign in on the web', x + pad, cy);
  cy += 12;

  if (card.qr) {
    const blockH = cy - blockTop;
    const qrY = blockTop + Math.max(0, (blockH - qrSize) / 2);
    doc.addImage(card.qr, 'PNG', x + w - pad - qrSize, qrY, qrSize, qrSize);
  }

  doc.setDrawColor(...NAVY);
  doc.setLineWidth(1.35);
  doc.setLineCap('round');
  doc.setLineDashPattern([0.01, 3.5], 0);
  doc.rect(x, y, w, h, 'S');
  doc.setLineDashPattern([], 0);
  doc.setLineCap('butt');
}

function drawLabel(doc, text, x, y) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(text, x, y, { baseline: 'top' });
  return y + 12;
}

function drawValueBox(doc, value, x, y, w) {
  const h = 28;
  doc.setLineDashPattern([], 0);
  doc.setLineCap('butt');
  doc.setDrawColor(...BOX_LINE);
  doc.setLineWidth(0.9);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 2, 2, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  let size = 14;
  const text = String(value);
  doc.setFontSize(size);
  while (size > 9 && doc.getTextWidth(text) > w - 16) {
    size -= 0.5;
    doc.setFontSize(size);
  }
  doc.text(text, x + w / 2, y + h / 2, { align: 'center', baseline: 'middle' });
  return y + h;
}

function drawStrong(doc, lines, x, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...NAVY);
  doc.text(lines, x, y, { baseline: 'top' });
  return y + lines.length * 14 + 2;
}

function drawHint(doc, text, x, y) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(text, x, y, { baseline: 'top' });
}
